import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { publicGet } from "../lib/apiPublic";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

function toRelativeMedia(url) {
  if (!url) return url;
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith("/media/")) return url;
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url.replace(/^https?:\/\/[^/]+/, "");
  }
  const trimmed = url.replace(/^\.?\/*/, "");
  return trimmed.startsWith("media/") ? `/${trimmed}` : `/media/${trimmed}`;
}

export default function MiniHostCard({ id, onViewProfile, autoMs = 3000 }) {
  const [host, setHost] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [hover, setHover] = React.useState(false);
  const [idx, setIdx] = React.useState(0);

  React.useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await publicGet(`/hosts/${id}/`, { params: { view: "card" } });
        if (!cancel) setHost(data);
      } catch (e) {
        if (!cancel) setHost(null);
        console.warn("MiniHostCard fetch failed", e);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [id]);

  // Collect & dedupe image URLs from venue_images
  const imageUrls = React.useMemo(() => {
    const imgs = Array.isArray(host?.venue_images) ? host.venue_images : [];
    const urls = imgs
      .map(v => v?.url || v?.image || v?.image_url)
      .filter(Boolean)
      .map(toRelativeMedia);
    const seen = new Set();
    return urls.filter(u => (seen.has(u) ? false : (seen.add(u), true)));
  }, [host]);

  // Keep index in range if images change
  React.useEffect(() => {
    if (!imageUrls.length) { setIdx(0); return; }
    setIdx((prev) => (prev >= imageUrls.length ? 0 : prev));
  }, [imageUrls]);

  // Auto-advance
  React.useEffect(() => {
    if (hover || imageUrls.length <= 1) return;
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % imageUrls.length);
    }, autoMs);
    return () => clearInterval(t);
  }, [imageUrls, hover, autoMs]);

  const goPrev = React.useCallback(() => {
    if (!imageUrls.length) return;
    setIdx((i) => (i - 1 + imageUrls.length) % imageUrls.length);
  }, [imageUrls]);

  const goNext = React.useCallback(() => {
    if (!imageUrls.length) return;
    setIdx((i) => (i + 1) % imageUrls.length);
  }, [imageUrls]);

  if (loading) {
    return (
      <Box sx={{ width: 240, p: 1, border: "1px solid #e5e7eb", borderRadius: 1, bgcolor: "#fff" }}>
        <Typography variant="body2" color="text.secondary">Loading venue…</Typography>
      </Box>
    );
  }
  if (!host) {
    return (
      <Box sx={{ width: 240, p: 1, border: "1px solid #e5e7eb", borderRadius: 1, bgcolor: "#fff" }}>
        <Typography variant="body2" color="error.main">Failed to load venue.</Typography>
      </Box>
    );
  }

  const name =
    host?.display_name ||
    `${host?.user?.first_name || ""} ${host?.user?.last_name || ""}`.trim() ||
    "Host";

  return (
    <Box sx={{ width: 240, p: 1, borderRadius: 1.5, backgroundColor: "rgba(95, 90, 90, 0.26)" }}>
      <Typography variant="body2" color="text.primary" sx={{ mb: 0}}>
        <strong>{name}</strong>
      </Typography>

      {/* Slider */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: 120,
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: "#f3f4f6",
          userSelect: "none",
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {imageUrls.length ? (
          <>
            <img
              key={imageUrls[idx]}
              src={imageUrls[idx]}
              alt={name}
              style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              loading="lazy"
              referrerPolicy="no-referrer"
            />

            {imageUrls.length > 1 && (
              <>
                <IconButton
                  aria-label="Previous"
                  onClick={goPrev}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 4,
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(255,255,255,0.9)",
                    "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    display: hover ? "flex" : "none",
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <IconButton
                  aria-label="Next"
                  onClick={goNext}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: 4,
                    transform: "translateY(-50%)",
                    bgcolor: "rgba(255,255,255,0.9)",
                    "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    display: hover ? "flex" : "none",
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </>
            )}

            {/* Dots */}
            {imageUrls.length > 1 && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 6,
                  left: "50%",
                  transform: "translateX(-50%)",
                  display: "flex",
                  gap: 0.75,
                }}
              >
                {imageUrls.map((_, i) => (
                  <Box
                    key={i}
                    onClick={() => setIdx(i)}
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: i === idx ? "common.white" : "rgba(255,255,255,0.6)",
                      border: "1px solid rgba(0,0,0,0.15)",
                      cursor: "pointer",
                    }}
                    title={`Image ${i + 1}`}
                  />
                ))}
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ width: "100%", height: "100%", bgcolor: "#f3f4f6" }} />
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
        <strong>Venue Type:</strong> {host.venue_type}<br/>
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0 }}>
        <strong>Art Preferences:</strong> {host.art_preferences?.join(", ")}
      </Typography>

      {onViewProfile && (
        <Box sx={{ mt: 1 }}>
          <button
            onClick={onViewProfile}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: "1px solid #faf8f8ff",
              background: "#aca7a7ee",
              cursor: "pointer",
            }}
          >
            View Profile
          </button>
        </Box>
      )} 
    </Box>
  );
}
